import React from 'react';
import {
  finiteStyleNumber,
  TOTAL_COLUMNS,
  resolveResponsiveBlockLayout,
  resolveResponsiveBlockTransform,
  resolveRuntimeViewport,
} from '../layoutEngine/blockFrame';
import { isBuilderAnchorBlock } from '../layoutEngine/blockPolicies/resolveBlockPolicy';
import { shouldSuppressBuilderHoverChrome } from '../layoutEngine/blockHoverChrome';
import { resolveBlockPolicy } from '../layoutEngine/blockPolicies/resolveBlockPolicy';
import { TextBlockRenderer } from '../blockRenderers/TextBlockRenderer';
import { ImageBlockRenderer } from '../blockRenderers/ImageBlockRenderer';
import { DecorativeAssetBlockRenderer } from '../blockRenderers/DecorativeAssetBlockRenderer';
import { ProductCardBlockRenderer } from '../blockRenderers/ProductCardBlockRenderer';
import {
  SpacerBlockRenderer,
  RequestFormBlockRenderer,
  FooterBlockRenderer,
  ProgressBlockRenderer,
  VideoBlockRenderer,
  SocialBlockRenderer,
  UnknownBlockRenderer,
} from '../blockRenderers/SimpleBlockRenderers';
import { ButtonBlockRenderer } from '../blockRenderers/ButtonBlockRenderer';
import { GalleryBlockRenderer } from '../blockRenderers/GalleryBlockRenderer';
import { ModelViewerBlockRenderer } from '../blockRenderers/ModelViewerBlockRenderer';
import { ConnectedBlockRenderer } from '../blockRenderers/ConnectedBlockRenderer';
import { isConnectedBlockType } from '../../flow/connectedBlocks';
import type { SectionElementCanvasBlockProps } from './SectionElementCanvasBlock.types';

export function SectionElementCanvasBlock({
  block,
  sectionId,
  settings,
  designSystemOverride,
  enquiryEmailTo,
  onSelectVase,
  onEnquirySubmit,
  onNavigateToPage,
  adminPreview,
  flow,
  isAdmin,
  isInteractPreview,
  isImmersivePreview,
  isImageAdjustMode,
  activeAdjustBlockId,
  moveMode,
  scaleMode,
  activeProjectionRows,
  activeImageInteraction,
  beginSurfaceMove,
  moveSurfaceMove,
  endSurfaceMove,
  handleWheelZoom,
  beginHandleInteraction,
  moveHandleInteraction,
  endHandleInteraction,
  editingTextBlockId,
  setTextMeasureNode,
  richTextFontOptions,
  typography,
  fontLibrary,
  useFluidSectionTypography,
  usesFixedFrameText,
  productRecords,
  selectedProductIds,
  layerSize,
  columnWidthPx,
  rowHeightPx,
  hoveredProductCardId,
  setHoveredProductCardId,
  productCardRuntimeSnapshotsRef,
  productCardWrapperNodesRef,
  productCardArticleNodesRef,
  productCardOverlayNodesRef,
  setProductCardMeasureNode,
  visibleColourCombinations,
  viewerRandomSelections,
  selectedBlockId,
  selectedBlockIds,
  showLayoutFrames = false,
  setActiveModelPreview,
  setConnectedBlockDebugSnapshot,
  renderLogicNodeIdTag,
  renderSelectionOverlay,
  showSelectionChrome = false,
  hoveredBlockId,
  setHoveredBlockId,
}: SectionElementCanvasBlockProps) {        const runtimeLayout = resolveResponsiveBlockLayout(
          block,
          isAdmin ? adminPreview?.viewport : undefined
        );
        const x = Math.max(1, runtimeLayout.x);
        const y = Math.max(1, runtimeLayout.y);
        const w = Math.max(1, runtimeLayout.w);
        const h = Math.max(1, runtimeLayout.h);
        const projectedWidth = `${(w / TOTAL_COLUMNS) * 100}%`;
        const projectedHeight = `${(h / activeProjectionRows) * 100}%`;
        const blockStyleMeta =
          block.style && typeof block.style === 'object'
            ? (block.style as Record<string, unknown>)
            : undefined;
        const explicitLayer = finiteStyleNumber(blockStyleMeta?.zIndex, NaN);
        const builderLayerLocked = isAdmin && blockStyleMeta?.builderLocked === true;
        const builderLayerHidden = isAdmin && blockStyleMeta?.builderHidden === true;
        const activeViewport = resolveRuntimeViewport(isAdmin ? adminPreview?.viewport : undefined);
        const isolateAdminViewport = isAdmin && Boolean(adminPreview?.viewport);
        const blockTransform = resolveResponsiveBlockTransform(blockStyleMeta, activeViewport, {
          isolateAdminViewport,
        });
        const transformOffset =
          typeof blockTransform.offsetXPx === 'number' || typeof blockTransform.offsetYPx === 'number'
            ? `translate(${blockTransform.offsetXPx ?? 0}px, ${blockTransform.offsetYPx ?? 0}px)`
            : blockTransform.offsetX || blockTransform.offsetY
              ? `translate(${blockTransform.offsetX}%, ${blockTransform.offsetY}%)`
              : undefined;
        const blockStyle: React.CSSProperties = {
          left: `${((x - 1) / TOTAL_COLUMNS) * 100}%`,
          top: `${((y - 1) / activeProjectionRows) * 100}%`,
          width:
            blockTransform.widthScale === 1
              ? projectedWidth
              : `calc(${projectedWidth} * ${blockTransform.widthScale})`,
          height:
            blockTransform.heightScale === 1
              ? projectedHeight
              : `calc(${projectedHeight} * ${blockTransform.heightScale})`,
          transform: transformOffset,
          zIndex: Number.isFinite(explicitLayer) ? explicitLayer : undefined,
          opacity: builderLayerHidden ? 0.16 : undefined,
          pointerEvents: builderLayerHidden ? 'none' : undefined,
        };
        const blockSelected = selectedBlockId === block.id || selectedBlockIds.has(block.id);
        const blockSurfaceClass = moveMode
          ? blockSelected
            ? 'cursor-default'
            : showLayoutFrames
              ? 'cursor-default ring-1 ring-blue-300/45'
              : 'cursor-default'
          : '';
        const hoverPointerHandlers =
          moveMode &&
          showSelectionChrome &&
          !shouldSuppressBuilderHoverChrome(block) &&
          setHoveredBlockId
            ? {
                onMouseEnter: () => setHoveredBlockId(block.id),
                onMouseLeave: () =>
                  setHoveredBlockId((current) => (current === block.id ? null : current)),
              }
            : {};
        const isHeroSlotBlock = blockStyleMeta?.__template_slot === 'hero';
        const blockPolicy = resolveBlockPolicy(block);
        const isSubscribeSlotBlock = blockStyleMeta?.__template_slot === 'subscribe';
        const isEnquirySlotBlock = blockStyleMeta?.__template_slot === 'enquiry';
        const heroSlotRole = typeof blockStyleMeta?.role === 'string' ? blockStyleMeta.role : '';
        const subscribeSlotRole = typeof blockStyleMeta?.role === 'string' ? blockStyleMeta.role : '';
        const enquirySlotRole = typeof blockStyleMeta?.role === 'string' ? blockStyleMeta.role : '';
        const blockSelectionHitProps = {
          'data-builder-block-id': block.id,
          'data-builder-block-type': block.type,
          'data-builder-selection-target': blockPolicy.selectionTarget,
          'data-builder-selection-hit': 'true',
        };
        const selectableBlockHitProps =
          builderLayerLocked || builderLayerHidden ? {} : blockSelectionHitProps;
        const blockWrapperProps = {
          className: `pointer-events-auto absolute ${blockSurfaceClass}`,
          style: blockStyle,
          'data-builder-block-id': block.id,
          'data-builder-block-type': block.type,
          'data-builder-block-root': 'true',
          ...hoverPointerHandlers,
        };

        if (block.type === 'text') {
          return (
            <TextBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              blockStyle={blockStyle}
              blockStyleMeta={blockStyleMeta}
              blockTransform={blockTransform}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              setTextMeasureNode={setTextMeasureNode}
              typography={typography}
              fontLibrary={fontLibrary}
              useFluidSectionTypography={useFluidSectionTypography}
              layerSize={layerSize}
              w={w}
              projectedWidth={projectedWidth}
              isAdmin={isAdmin}
              isInteractPreview={isInteractPreview}
              editingTextBlockId={editingTextBlockId}
              blockSelected={blockSelected}
              isHeroSlotBlock={isHeroSlotBlock}
              heroSlotRole={heroSlotRole}
              isSubscribeSlotBlock={isSubscribeSlotBlock}
              subscribeSlotRole={subscribeSlotRole}
              isEnquirySlotBlock={isEnquirySlotBlock}
              enquirySlotRole={enquirySlotRole}
              usesFixedFrameText={usesFixedFrameText}
              adminPreview={adminPreview}
              richTextFontOptions={richTextFontOptions}
            />
          );
        }
        if (block.type === 'image') {
          return (
            <ImageBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              fontLibrary={fontLibrary}
              isAdmin={isAdmin}
              isHeroSlotBlock={isHeroSlotBlock}
              heroSlotRole={heroSlotRole}
              isImageAdjustMode={isImageAdjustMode}
              activeAdjustBlockId={activeAdjustBlockId}
              activeImageInteraction={activeImageInteraction}
              onExitImageAdjust={adminPreview?.onExitImageAdjust}
              beginSurfaceMove={beginSurfaceMove}
              moveSurfaceMove={moveSurfaceMove}
              endSurfaceMove={endSurfaceMove}
              handleWheelZoom={handleWheelZoom}
              beginHandleInteraction={beginHandleInteraction}
              moveHandleInteraction={moveHandleInteraction}
              endHandleInteraction={endHandleInteraction}
            />
          );
        }

        if (block.type === 'decorative-asset') {
          return (
            <DecorativeAssetBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
            />
          );
        }

        if (block.type === 'product-card') {
          return (
            <ProductCardBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              renderSelectionOverlay={renderSelectionOverlay}
              logicNodeIdTag={renderLogicNodeIdTag(block)}
              productRecords={productRecords}
              selectedProductIds={selectedProductIds}
              designSystemOverride={designSystemOverride}
              settings={settings}
              w={w}
              h={h}
              x={x}
              y={y}
              columnWidthPx={columnWidthPx}
              rowHeightPx={rowHeightPx}
              scaleMode={scaleMode}
              isAdmin={isAdmin}
              isInteractPreview={isInteractPreview}
              activeProjectionRows={activeProjectionRows}
              layerSize={layerSize}
              fontLibrary={fontLibrary}
              typography={typography}
              useFluidSectionTypography={useFluidSectionTypography}
              hoveredProductCardId={hoveredProductCardId}
              setHoveredProductCardId={setHoveredProductCardId}
              productCardRuntimeSnapshotsRef={productCardRuntimeSnapshotsRef}
              productCardWrapperNodesRef={productCardWrapperNodesRef}
              productCardArticleNodesRef={productCardArticleNodesRef}
              productCardOverlayNodesRef={productCardOverlayNodesRef}
              setProductCardMeasureNode={setProductCardMeasureNode}
              flow={flow}
              sectionId={sectionId}
              adminPreview={adminPreview}
              onSelectVase={onSelectVase}
              setActiveModelPreview={setActiveModelPreview}
            />
          );
        }
        if (block.type === 'spacer') {
          return (
            <SpacerBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              fontLibrary={fontLibrary}
              isAdmin={isAdmin}
            />
          );
        }

        if (block.type === 'button') {
          return (
            <ButtonBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              logicNodeTag={renderLogicNodeIdTag(block)}
              flow={flow}
              sectionId={sectionId}
              isAdmin={isAdmin}
              isInteractPreview={isInteractPreview}
              isHeroSlotBlock={isHeroSlotBlock}
              heroSlotRole={heroSlotRole}
              isSubscribeSlotBlock={isSubscribeSlotBlock}
              subscribeSlotRole={subscribeSlotRole}
              isEnquirySlotBlock={isEnquirySlotBlock}
              enquirySlotRole={enquirySlotRole}
              typography={typography}
              fontLibrary={fontLibrary}
              useFluidSectionTypography={useFluidSectionTypography}
              onNavigateToPage={onNavigateToPage}
              onOpenModelPreview={setActiveModelPreview}
            />
          );
        }

        if (block.type === 'gallery') {
          return (
            <GalleryBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              flow={flow}
              sectionId={sectionId}
              productRecords={productRecords}
              selectedProductIds={selectedProductIds}
              moveMode={moveMode}
              isAdmin={isAdmin}
              isInteractPreview={isInteractPreview}
              onSelectBlock={adminPreview?.onSelectBlock}
              onOpenBlockContent={adminPreview?.onOpenBlockContent}
              onSelectVase={onSelectVase}
              onOpenModelPreview={setActiveModelPreview}
            />
          );
        }

        if (block.type === 'model-viewer') {
          return (
            <ModelViewerBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              blockStyle={blockStyle}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              isAdmin={isAdmin}
              isInteractPreview={isInteractPreview}
              isImmersivePreview={isImmersivePreview}
              isLayoutEditing={moveMode}
              showLayoutFrames={showLayoutFrames}
              adminViewport={adminPreview?.viewport}
              blockSelected={blockSelected}
              visibleColourCombinations={visibleColourCombinations}
              randomSelection={viewerRandomSelections[block.id]}
            />
          );
        }

        if (isConnectedBlockType(block.type)) {
          return (
            <ConnectedBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              logicNodeIdTag={renderLogicNodeIdTag(block)}
              sectionId={sectionId}
              isAdmin={isAdmin}
              isInteractPreview={isInteractPreview}
              fontLibrary={fontLibrary}
              renderSelectionOverlay={renderSelectionOverlay}
              onDebugSnapshot={setConnectedBlockDebugSnapshot}
            />
          );
        }

        if (block.type === 'request-form') {
          return (
            <RequestFormBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              logicNodeTag={renderLogicNodeIdTag(block)}
              sectionId={sectionId}
              enquiryEmailTo={enquiryEmailTo}
              onEnquirySubmit={onEnquirySubmit}
              isAdmin={isAdmin}
              isInteractPreview={isInteractPreview}
            />
          );
        }

        if (block.type === 'site-footer') {
          return (
            <FooterBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
            />
          );
        }

        if (block.type === 'site-progress') {
          return (
            <ProgressBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              flowState={flow.flowState}
              flows={flow.flows}
              isAdmin={isAdmin}
              isInteractPreview={isInteractPreview}
              isImmersivePreview={isImmersivePreview}
            />
          );
        }

        if (block.type === 'video') {
          return (
            <VideoBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
            />
          );
        }

        if (block.type === 'social') {
          return (
            <SocialBlockRenderer
              key={block.id}
              block={block}
              blockWrapperProps={blockWrapperProps}
              selectableBlockHitProps={selectableBlockHitProps}
              selectionTarget={blockPolicy.selectionTarget}
              renderSelectionOverlay={renderSelectionOverlay}
              fontLibrary={fontLibrary}
            />
          );
        }

        return (
          <UnknownBlockRenderer
            key={block.id}
            block={block}
            blockWrapperProps={blockWrapperProps}
            selectableBlockHitProps={selectableBlockHitProps}
            selectionTarget={blockPolicy.selectionTarget}
            renderSelectionOverlay={renderSelectionOverlay}
          />
        );
}
